import { useEffect, useMemo, useState } from "react";
import { Trash2 } from "lucide-react";
import { api } from "../api";
import StaffLayout from "../StaffLayout";

const inputCls =
  "px-3 py-2 bg-bone border border-ink/15 focus:border-matcha-dark outline-none rounded-sm text-ink text-sm";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function formatErr(detail) {
  if (!detail) return "";
  if (typeof detail === "string") return detail;
  return JSON.stringify(detail);
}

export default function Usage() {
  const [date, setDate] = useState(todayISO());
  const [ingredients, setIngredients] = useState([]);
  const [qtyMap, setQtyMap] = useState({}); // {ingredient_id: "10"}
  const [logs, setLogs] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [savedFlash, setSavedFlash] = useState("");

  const loadIngredients = async () => {
    const { data } = await api.get("/ingredients/public");
    setIngredients(data);
  };
  const loadLogs = async (d = date) => {
    const { data } = await api.get(`/usage?on_date=${d}`);
    setLogs(data);
  };
  useEffect(() => {
    loadIngredients();
    loadLogs(date);
    // eslint-disable-next-line
  }, [date]);

  const totalsForDate = useMemo(() => {
    const map = {};
    logs.forEach((l) => {
      map[l.ingredient_id] = (map[l.ingredient_id] || 0) + Number(l.qty || 0);
    });
    return map;
  }, [logs]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSavedFlash("");
    const items = ingredients
      .map((i) => ({ ingredient_id: i.id, qty: Number(qtyMap[i.id] || 0) }))
      .filter((it) => it.qty > 0);
    if (items.length === 0) {
      setErr("Enter at least one quantity.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/usage", { date, items });
      setQtyMap({});
      await Promise.all([loadIngredients(), loadLogs(date)]);
      setSavedFlash(`Logged ${data.logged.length} entries.`);
      setTimeout(() => setSavedFlash(""), 3000);
    } catch (e2) {
      setErr(formatErr(e2?.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this usage entry? Stock will be restored.")) return;
    await api.delete(`/usage/${id}`);
    await Promise.all([loadIngredients(), loadLogs(date)]);
  };

  return (
    <StaffLayout>
      <div className="mb-8" data-testid="usage-page-header">
        <p className="overline text-matcha-dark mb-2">— Daily Usage</p>
        <h1 className="font-display text-3xl sm:text-4xl text-ink" style={{ letterSpacing: "0.04em" }}>
          Log Daily Usage
        </h1>
        <p className="text-ink-soft text-sm mt-2 max-w-xl">
          Record how much of each ingredient was used today (milk, oatside, matcha, etc.). Stock is deducted automatically.
        </p>
      </div>

      <div className="flex items-center gap-4 mb-6 flex-wrap">
        <label className="overline text-ink-soft">Date</label>
        <input
          data-testid="usage-date"
          type="date"
          className={inputCls}
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
        {savedFlash && (
          <span className="text-xs uppercase tracking-[0.18em] text-matcha-dark" data-testid="usage-saved-flash">
            ✓ {savedFlash}
          </span>
        )}
      </div>

      <div className="grid md:grid-cols-12 gap-6">
        <form onSubmit={submit} className="md:col-span-7 bg-bone border border-ink/10 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-sage/40">
              <tr className="text-left text-ink-soft">
                <th className="px-4 py-3 overline">Ingredient</th>
                <th className="px-4 py-3 overline text-right">In Stock</th>
                <th className="px-4 py-3 overline text-right">Used Today</th>
                <th className="px-4 py-3 overline text-right">Use Now</th>
              </tr>
            </thead>
            <tbody>
              {ingredients.map((i) => {
                const inStock = Number(i.stock_qty || 0);
                const low = i.low_stock_threshold > 0 && inStock <= i.low_stock_threshold;
                const usedToday = totalsForDate[i.id] || 0;
                return (
                  <tr key={i.id} className="border-t border-ink/5" data-testid={`usage-ing-row-${i.id}`}>
                    <td className="px-4 py-3">
                      <span className="text-ink">{i.name}</span>
                      <span className="ml-2 text-[10px] uppercase tracking-[0.18em] text-ink-soft">({i.unit})</span>
                    </td>
                    <td className={`px-4 py-3 text-right whitespace-nowrap ${low ? "text-red-700" : "text-matcha-dark"}`}>
                      {inStock} {i.unit}
                      {low && <span className="ml-2 text-[10px] uppercase tracking-[0.18em]">low</span>}
                    </td>
                    <td className="px-4 py-3 text-right text-ink-soft whitespace-nowrap">
                      {usedToday > 0 ? `${usedToday} ${i.unit}` : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <input
                        data-testid={`usage-input-${i.id}`}
                        type="number"
                        step="0.001"
                        min="0"
                        placeholder="0"
                        className={inputCls + " w-24 text-right"}
                        value={qtyMap[i.id] ?? ""}
                        onChange={(e) => setQtyMap((s) => ({ ...s, [i.id]: e.target.value }))}
                      />
                    </td>
                  </tr>
                );
              })}
              {ingredients.length === 0 && (
                <tr><td colSpan={4} className="p-8 text-center text-ink-soft">No ingredients defined. Ask admin to add some.</td></tr>
              )}
            </tbody>
          </table>
          <div className="px-4 py-4 bg-cream/30 border-t border-ink/5 flex items-center justify-between gap-3 flex-wrap">
            {err && <p className="text-sm text-red-700 bg-red-50 px-3 py-1.5 rounded-sm">{err}</p>}
            <button
              data-testid="usage-submit"
              type="submit"
              disabled={busy || ingredients.length === 0}
              className="ml-auto inline-flex items-center gap-2 rounded-full px-6 py-2 bg-ink text-bone text-xs uppercase tracking-[0.22em] hover:bg-matcha-dark transition-colors disabled:opacity-50"
            >
              {busy ? "Saving…" : "Log Usage"}
            </button>
          </div>
        </form>

        <div className="md:col-span-5">
          <div className="bg-bone border border-ink/10 rounded-sm p-5">
            <p className="overline text-matcha-dark mb-3">Today&apos;s Entries · {date}</p>
            {logs.length === 0 && (
              <p className="text-sm text-ink-soft">No usage logged yet for this date.</p>
            )}
            <ul className="space-y-2 max-h-[480px] overflow-y-auto">
              {logs.map((l) => (
                <li
                  key={l.id}
                  data-testid={`usage-log-row-${l.id}`}
                  className="flex items-start justify-between gap-3 border-b border-ink/5 pb-2 last:border-0"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-ink">
                      {l.ingredient_name} <span className="text-matcha-dark">{l.qty} {l.unit}</span>
                    </p>
                    <p className="text-[10px] text-ink-soft uppercase tracking-[0.18em]">
                      {l.logged_by_name}{l.notes ? ` · ${l.notes}` : ""}
                    </p>
                  </div>
                  <button
                    data-testid={`usage-del-${l.id}`}
                    onClick={() => del(l.id)}
                    className="text-ink-soft hover:text-red-600"
                  >
                    <Trash2 size={14} />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </StaffLayout>
  );
}
